import React from 'react';
import { Box, Container, Typography, styled, Stack, Chip, Button, Paper, Avatar, alpha } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Icons
import PsychologyIcon from '@mui/icons-material/Psychology';
import HubIcon from '@mui/icons-material/Hub';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const SectionWrapper = styled(Box)({
  padding: '120px 0',
  background: '#020617', // Slate 950 deep background
  color: '#f8fafc',
  position: 'relative',
  overflow: 'hidden',
});

const GlassCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.65)',
  backdropFilter: 'blur(16px)',
  borderRadius: '20px',
  border: '1px solid rgba(245, 158, 11, 0.15)',
  padding: '24px',
  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  '&:hover': {
    border: '1px solid rgba(245, 158, 11, 0.4)',
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 30px -10px rgba(245, 158, 11, 0.2)',
    background: 'rgba(15, 23, 42, 0.85)',
  }
}));

const FeaturePoint = ({ text }) => (
  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
    <CheckCircleOutlineIcon sx={{ color: '#f59e0b', fontSize: '1.1rem' }} />
    <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: 500 }}>
      {text}
    </Typography>
  </Stack>
);

const ConsultDetail = () => {
  const navigate = useNavigate();

  const pillars = [
    {
      title: "System Architecture & Stack Selection",
      icon: <HubIcon sx={{ color: '#f59e0b' }} />,
      points: ["Scalable Microservices / Monoliths", "Cloud Native Migration (AWS/GCP)", "Database Schema Design"]
    },
    {
      title: "Performance & Code Quality Audits",
      icon: <QueryStatsIcon sx={{ color: '#fbbf24' }} />,
      points: ["Bottleneck Identification", "CI/CD Pipeline Automation", "Refactoring Strategy"]
    },
    {
      title: "Security & Compliance Oversight",
      icon: <SecurityIcon sx={{ color: '#f59e0b' }} />,
      points: ["API Security & Auth (OAuth/JWT)", "Data Encryption Standards", "Zero-Trust Principles"]
    },
    {
      title: "Cost Optimization & Scalability",
      icon: <SpeedIcon sx={{ color: '#fbbf24' }} />,
      points: ["Cloud Bill Reduction", "Infrastructure Bottlenecks", "Zero-Downtime Scaling"]
    }
  ];

  return (
    <SectionWrapper id="consult-detail">
      {/* Ambient Radial Gradient Blur Backgrounds */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '-10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(245, 158, 11, 0.12) 0%, transparent 70%)',
          filter: 'blur(90px)',
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '-5%',
          width: '450px',
          height: '450px',
          background: 'radial-gradient(circle, rgba(251, 191, 36, 0.08) 0%, transparent 70%)',
          filter: 'blur(100px)',
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          
          {/* Left Side: Strategic Content */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Chip
                label="TECH STRATEGY & ARCHITECTURE"
                sx={{
                  bgcolor: alpha('#f59e0b', 0.1),
                  color: '#f59e0b',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: 1.5,
                  mb: 2.5,
                  px: 1,
                  py: 0.5
                }}
              />
              
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2.5,
                  fontSize: { xs: '2.1rem', sm: '2.8rem', md: '3.2rem' },
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  color: '#f8fafc'
                }}
              >
                Architectural Precision.{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}
                >
                  Strategic Growth.
                </Box>
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: '#94a3b8',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  lineHeight: 1.7,
                  maxWidth: '560px'
                }}
              >
                Strategy and architectural clarity dictate whether software scales or accumulates unmanageable technical debt. I deliver actionable IT roadmaps, system audits, and tech stack choices designed for high uptime, cost efficiency, and operational speed.
              </Typography>

              {/* Action Button */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/contact')}
                  sx={{
                    py: 1.6,
                    px: 3.5,
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: '#020617',
                    boxShadow: '0 8px 25px -5px rgba(245, 158, 11, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                      boxShadow: '0 12px 30px -5px rgba(245, 158, 11, 0.6)',
                    }
                  }}
                >
                  Schedule Technical Audit
                </Button>
              </Stack>

              {/* Expertise Highlights Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  background: 'rgba(245, 158, 11, 0.04)',
                  borderRadius: '18px',
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  backdropFilter: 'blur(12px)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2
                }}
              >
                <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.15), width: 44, height: 44 }}>
                  <PsychologyIcon sx={{ color: '#f59e0b' }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 700, mb: 0.5 }}>
                    Decisive Problem Solving
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.5, fontSize: '0.875rem' }}>
                    From high-growth startups to enterprise logistics, I eliminate architectural bottlenecks and align technical capabilities directly with business goals.
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Right Side: 2x2 Pillars Grid Overlaying Modern Image Framing */}
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Grid container spacing={4}>
                {pillars.map((pillar, idx) => (
                  <Grid size={{ xs: 4, sm: 6}} key={idx}>
                    <GlassCard>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: alpha('#f59e0b', 0.12), width: 40, height: 40, borderRadius: '12px' }}>
                          {pillar.icon}
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.3 }}>
                          {pillar.title}
                        </Typography>
                      </Box>
                      {pillar.points.map((pt, pIdx) => (
                        <FeaturePoint key={pIdx} text={pt} />
                      ))}
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>

              {/* Live Metric Banner floating below the grid */}
              <Paper
                elevation={0}
                sx={{
                  mt: 8,
                  p: 2.5,
                  borderRadius: '16px',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  justifyContent: 'space-around',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                    100%
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                    Tailored Tech Roadmaps
                  </Typography>
                </Box>
                <Box sx={{ width: '1px', height: '30px', bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#fbbf24' }}>
                    Zero
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                    Vendor Lock-In Bias
                  </Typography>
                </Box>
                <Box sx={{ width: '1px', height: '30px', bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                    &lt; 24h
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                    Scoping Response
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

        </Grid>
      </Container>
    </SectionWrapper>
  );
};

export default ConsultDetail;